import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

function GeographicChart({ data }) {
  if (!data) {
    return <div className="text-center py-8 text-gray-500">No geographic data available</div>;
  }

  const { countries = [], regions = [], cities = [] } = data;

  // Colors for charts
  const COLORS = [
    '#3b82f6',
    '#10b981',
    '#f59e0b',
    '#ef4444',
    '#8b5cf6',
    '#06b6d4',
    '#84cc16',
    '#f97316',
    '#ec4899',
    '#6366f1',
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          <p className="text-sm text-blue-600">Visitors: {payload[0].value}</p>
        </div>
      );
    }
    return null;
  };

  // Get country name from country code
  const getCountryName = code => {
    const countryNames = {
      US: 'United States',
      GB: 'United Kingdom',
      CA: 'Canada',
      AU: 'Australia',
      DE: 'Germany',
      FR: 'France',
      JP: 'Japan',
      CN: 'China',
      IN: 'India',
      BR: 'Brazil',
      MX: 'Mexico',
      ES: 'Spain',
      IT: 'Italy',
      NL: 'Netherlands',
      SE: 'Sweden',
      NO: 'Norway',
      DK: 'Denmark',
      FI: 'Finland',
      PL: 'Poland',
      RU: 'Russia',
      KR: 'South Korea',
      SG: 'Singapore',
      HK: 'Hong Kong',
      TW: 'Taiwan',
      TH: 'Thailand',
      MY: 'Malaysia',
      ID: 'Indonesia',
      PH: 'Philippines',
      VN: 'Vietnam',
      NZ: 'New Zealand',
      ZA: 'South Africa',
      EG: 'Egypt',
      NG: 'Nigeria',
      KE: 'Kenya',
      GH: 'Ghana',
      MA: 'Morocco',
      TN: 'Tunisia',
      DZ: 'Algeria',
      AR: 'Argentina',
      CL: 'Chile',
      CO: 'Colombia',
      PE: 'Peru',
      VE: 'Venezuela',
      UY: 'Uruguay',
      PY: 'Paraguay',
      BO: 'Bolivia',
      EC: 'Ecuador',
      CR: 'Costa Rica',
      PA: 'Panama',
      GT: 'Guatemala',
      SV: 'El Salvador',
      HN: 'Honduras',
      NI: 'Nicaragua',
      BZ: 'Belize',
      JM: 'Jamaica',
      TT: 'Trinidad and Tobago',
      BB: 'Barbados',
      BS: 'Bahamas',
      CU: 'Cuba',
      DO: 'Dominican Republic',
      HT: 'Haiti',
      PR: 'Puerto Rico',
      VI: 'Virgin Islands',
      AW: 'Aruba',
      CW: 'Curaçao',
      SX: 'Sint Maarten',
      AG: 'Antigua and Barbuda',
      DM: 'Dominica',
      GD: 'Grenada',
      KN: 'Saint Kitts and Nevis',
      LC: 'Saint Lucia',
      VC: 'Saint Vincent and the Grenadines',
      AI: 'Anguilla',
      VG: 'British Virgin Islands',
      KY: 'Cayman Islands',
      TC: 'Turks and Caicos Islands',
      MS: 'Montserrat',
      FK: 'Falkland Islands',
      GF: 'French Guiana',
      GY: 'Guyana',
      SR: 'Suriname',
      GL: 'Greenland',
      IS: 'Iceland',
      IE: 'Ireland',
      PT: 'Portugal',
      GR: 'Greece',
      CY: 'Cyprus',
      MT: 'Malta',
      LU: 'Luxembourg',
      BE: 'Belgium',
      CH: 'Switzerland',
      AT: 'Austria',
      CZ: 'Czech Republic',
      SK: 'Slovakia',
      HU: 'Hungary',
      RO: 'Romania',
      BG: 'Bulgaria',
      HR: 'Croatia',
      SI: 'Slovenia',
      BA: 'Bosnia and Herzegovina',
      RS: 'Serbia',
      ME: 'Montenegro',
      MK: 'North Macedonia',
      AL: 'Albania',
      XK: 'Kosovo',
      MD: 'Moldova',
      UA: 'Ukraine',
      BY: 'Belarus',
      LT: 'Lithuania',
      LV: 'Latvia',
      EE: 'Estonia',
      TR: 'Turkey',
      IL: 'Israel',
      JO: 'Jordan',
      LB: 'Lebanon',
      SY: 'Syria',
      IQ: 'Iraq',
      IR: 'Iran',
      SA: 'Saudi Arabia',
      AE: 'United Arab Emirates',
      QA: 'Qatar',
      KW: 'Kuwait',
      BH: 'Bahrain',
      OM: 'Oman',
      YE: 'Yemen',
      AF: 'Afghanistan',
      PK: 'Pakistan',
      BD: 'Bangladesh',
      LK: 'Sri Lanka',
      NP: 'Nepal',
      BT: 'Bhutan',
      MV: 'Maldives',
      MM: 'Myanmar',
      LA: 'Laos',
      KH: 'Cambodia',
      BN: 'Brunei',
      TL: 'East Timor',
      PG: 'Papua New Guinea',
      FJ: 'Fiji',
      VU: 'Vanuatu',
      NC: 'New Caledonia',
      PF: 'French Polynesia',
      WS: 'Samoa',
      TO: 'Tonga',
      TV: 'Tuvalu',
      KI: 'Kiribati',
      NR: 'Nauru',
      PW: 'Palau',
      FM: 'Micronesia',
      MH: 'Marshall Islands',
      SB: 'Solomon Islands',
      CK: 'Cook Islands',
      NU: 'Niue',
      TK: 'Tokelau',
      WF: 'Wallis and Futuna',
      AS: 'American Samoa',
      GU: 'Guam',
      MP: 'Northern Mariana Islands',
    };

    return countryNames[code] || code;
  };

  // Prepare data for charts
  const countryData = countries.slice(0, 10).map(country => ({
    ...country,
    name: getCountryName(country.country),
  }));

  const regionData = regions.slice(0, 10);
  const cityData = cities.slice(0, 10);

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2V3.935M8 3.935A2.5 2.5 0 0110.5 3h3A2.5 2.5 0 0116 3.935M8 3.935A2.5 2.5 0 0010.5 3h3A2.5 2.5 0 0016 3.935"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-blue-600">Countries</p>
              <p className="text-2xl font-bold text-blue-900">{countries.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg
                className="w-5 h-5 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-600">Regions</p>
              <p className="text-2xl font-bold text-green-900">{regions.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg
                className="w-5 h-5 text-purple-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-purple-600">Cities</p>
              <p className="text-2xl font-bold text-purple-900">{cities.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Top Countries */}
      {countryData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bar Chart */}
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={countryData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Pie Chart */}
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={countryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="count"
                  >
                    {countryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Top Regions */}
      {regionData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Regions</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={regionData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="region"
                  stroke="#6b7280"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis stroke="#6b7280" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top Cities */}
      {cityData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Cities</h3>
          <div className="space-y-3">
            {cityData.map((city, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-bold text-purple-600">{index + 1}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{city.city}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-sm text-gray-500">{city.count} visitors</span>
                  <div className="w-24 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-600 h-2 rounded-full"
                      style={{
                        width: `${Math.min(100, (city.count / cityData[0].count) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GeographicChart;
